import { SessionType } from '@prisma/client'
import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { DevSettingsPanel } from './components/dev-settings-panel.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { ProgressBar } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { Toaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import focusModeStyleSheetUrl from './styles/focus-mode.css?url'
import spotlightStyleSheetUrl from './styles/spotlight.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { FocusModeProvider, useFocusMode } from './utils/focus-mode-context.tsx'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: spotlightStyleSheetUrl },
		{ rel: 'stylesheet', href: focusModeStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: data ? ENV.SITE_TITLE : 'Error | ' + ENV.SITE_TITLE,
		},
		{ name: 'description', content: ENV.SITE_DESCRIPTION },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function BackgroundEffects() {
	const { mode } = useFocusMode()
	const hasSession = mode !== null
	const isFocus = mode === SessionType.focus
	const isAmbient = mode === SessionType.ambient
	const isMeditate = mode === SessionType.meditate

	return (
		<>
			{/* Colored gradient overlay */}
			<div
				className={`pointer-events-none absolute inset-0 transition-opacity duration-[3000ms] ${
					hasSession ? 'opacity-100' : 'opacity-0'
				}`}
			>
				<div
					className={`absolute inset-0 transition-opacity duration-[2500ms] ${
						isAmbient ? 'opacity-100' : 'opacity-0'
					}`}
					style={{
						background:
							'linear-gradient(to bottom right, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.15))',
					}}
				/>
				<div
					className={`absolute inset-0 transition-opacity duration-[2500ms] ${
						isMeditate ? 'opacity-100' : 'opacity-0'
					}`}
					style={{
						background:
							'linear-gradient(to bottom right, rgba(168, 85, 247, 0.15), rgba(236, 72, 153, 0.15))',
					}}
				/>
				<div
					className={`absolute inset-0 transition-opacity duration-[2500ms] ${
						isFocus ? 'opacity-100' : 'opacity-0'
					}`}
					style={{
						background:
							'linear-gradient(to bottom right, rgba(59, 130, 246, 0.15), rgba(99, 102, 241, 0.15))',
					}}
				/>
			</div>

			{/* Theater-like dimming overlay */}
			<div
				className={`pointer-events-none absolute inset-0 bg-black transition-opacity duration-[3000ms] ${
					isFocus ? 'opacity-60 dark:opacity-70' : 'opacity-0'
				}`}
			/>

			{/* Spotlight effect for focus mode */}
			<div className="pointer-events-none absolute inset-0 overflow-hidden">
				{/* Outer spotlight layer */}
				<div
					className={`absolute top-1/2 left-1/2 h-[150%] w-[150%] transition-all duration-[3000ms] ${
						isFocus
							? 'scale-100 opacity-30 dark:opacity-20'
							: 'scale-150 opacity-0'
					}`}
					style={{
						background: `radial-gradient(ellipse at center,
							rgba(59, 130, 246, 0.5) 0%,
							rgba(59, 130, 246, 0.3) 25%,
							rgba(59, 130, 246, 0.1) 50%,
							transparent 70%
						)`,
						filter: 'blur(40px)',
						transform: 'translate(-50%, -50%)',
					}}
				/>
				{/* Inner rotating spotlight */}
				<div
					className={`absolute top-1/2 left-1/2 h-[100%] w-[100%] transition-all duration-[3000ms] ${
						isFocus
							? 'spotlight-rotate opacity-40 dark:opacity-30'
							: 'opacity-0'
					}`}
					style={{
						background: `radial-gradient(circle at center,
							rgba(147, 197, 253, 0.6) 0%,
							rgba(147, 197, 253, 0.3) 30%,
							transparent 60%
						)`,
						filter: 'blur(60px)',
					}}
				/>
				{/* Focused beam effect */}
				<div
					className={`absolute top-1/2 left-1/2 h-[60%] w-[60%] transition-all duration-[3000ms] ${
						isFocus
							? 'scale-100 opacity-50 dark:opacity-40'
							: 'scale-50 opacity-0'
					}`}
					style={{
						background: `radial-gradient(circle at center,
							rgba(219, 234, 254, 0.8) 0%,
							rgba(191, 219, 254, 0.4) 20%,
							transparent 50%
						)`,
						filter: 'blur(30px)',
						transform: 'translate(-50%, -50%)',
					}}
				/>
			</div>
		</>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const { mode } = useFocusMode()
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div
				className={`relative min-h-screen bg-gray-50 dark:bg-gray-900 ${mode === SessionType.focus ? 'focus-mode' : ''}`}
			>
				<BackgroundEffects />

				<div className="relative z-10 flex min-h-screen flex-col">
					<header className="bg-transparent">
						<nav className="container flex items-center justify-between py-5">
							<Logo />
							{user ? (
								<UserDropdown />
							) : (
								<NonAuthMenu userPreference={data.requestInfo.userPrefs.theme} />
							)}
						</nav>
					</header>

					<div className="flex flex-1 flex-col">
						<Outlet />
					</div>
				</div>
			</div>
			<Toaster closeButton position="top-center" theme={theme} />
			<ProgressBar />
			<DevSettingsPanel />
		</OpenImgContextProvider>
	)
}

function Logo() {
	const { mode } = useFocusMode()
	const isLightMode = mode === SessionType.ambient // ambient mode should always show dark text
	const isDarkMode = mode === SessionType.focus // focus mode should always show white text

	return (
		<Link to="/" className="group grid leading-snug">
			<span
				className={`font-medium transition-colors duration-[3000ms] ${
					isDarkMode
						? 'text-white/60'
						: isLightMode
							? 'text-black/60'
							: 'text-black/60 dark:text-white/60' // default: follow theme
				}`}
			>
				{ENV.SITE_TITLE.toLowerCase()}
			</span>
		</Link>
	)
}

function NonAuthMenu({ userPreference }: { userPreference?: Theme | null }) {
	const { mode } = useFocusMode()
	const isLightMode = mode === SessionType.ambient
	const isDarkMode = mode === SessionType.focus

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					className={`rounded-full backdrop-blur transition-all duration-[3000ms] ${
						isDarkMode
							? 'border-white/30 bg-black/10 hover:bg-black/20'
							: isLightMode
								? 'border-black/30 bg-white/10 hover:bg-white/20'
								: 'border-black/30 dark:border-white/30 bg-white/10 dark:bg-black/10 hover:bg-white/20 dark:hover:bg-black/20'
					}`}
				>
					<Icon name="menu" className={`h-5 w-5 transition-colors duration-[3000ms] ${
						isDarkMode
							? 'text-white/60'
							: isLightMode
								? 'text-black/60'
								: 'text-black/60 dark:text-white/60'
					}`} />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end">
					<DropdownMenuItem asChild>
						<Link to="/login" className="flex items-center gap-2">
							<Icon name="arrow-right" className="h-4 w-4" />
							Login
						</Link>
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem asChild>
						<div className="flex items-center justify-center px-2 py-1.5">
							<ThemeSwitch userPreference={userPreference} />
						</div>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<FocusModeProvider>
				<App />
			</FocusModeProvider>
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
